import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Users,
  Zap,
  TrendingUp,
  BookOpen,
  Brain,
  Award,
  Clock,
  Monitor,
  Code2,
  Database,
  Globe,
  Cpu,
  FileCode,
} from "lucide-react";
import FAQAccordion from "../components/FAQAccordion";

const PRIMARY = "#0097b2";
const SECONDARY = "#fcbf0a";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.1 * i, duration: 0.5 },
  }),
};

const openAdmissionEnquiry = () => {
  const phoneNumber = "919918519991";
  const message =
    "Hello SSS Achievers, I would like to enquire about the Computer Science course. Please share complete details. Thank you.";
  window.open(
    `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`,
    "_blank",
  );
};

export default function ComputerScience() {
  return (
    <>
      <Helmet>
        <title>Computer Science Coaching in Ghazipur – Class 8 to 12 | SSS Achievers</title>
        <meta
          name="description"
          content="SSS Achievers Computer Science coaching in Ghazipur for Class 8–12. Learn programming, MS Office, web basics, Python, C++, AI & digital skills. Expert faculty, small batches, free demo class. Best computer coaching in Ghazipur, UP."
        />
        <meta
          name="keywords"
          content="Computer Science coaching Ghazipur, computer classes Ghazipur, CS coaching UP, programming classes Ghazipur, Python coaching Ghazipur, CBSE Computer Science Ghazipur, IT coaching Ghazipur, SSS Achievers computer course, Class 10 computer coaching Ghazipur"
        />
        <link rel="canonical" href="https://www.sssachievers.com/courses/computer-science" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Computer Science Coaching Ghazipur – Class 8 to 12 | SSS Achievers" />
        <meta property="og:description" content="Learn Computer Science at SSS Achievers Ghazipur. Programming, MS Office, Python, web basics & CBSE CS curriculum. Expert faculty & free demo class." />
        <meta property="og:url" content="https://www.sssachievers.com/courses/computer-science" />
        <meta property="og:image" content="https://www.sssachievers.com/logo512.png" />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:site_name" content="SSS Achievers" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Computer Science Coaching Ghazipur | SSS Achievers" />
        <meta name="twitter:description" content="Best CS coaching in Ghazipur. Python, programming, CBSE CS. Expert faculty, small batches. Free demo at SSS Achievers." />
        <meta name="twitter:image" content="https://www.sssachievers.com/logo512.png" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Course",
            "name": "Computer Science Course – Class 8 to 12",
            "description": "Computer Science coaching in Ghazipur covering programming, MS Office, Python, C++, web fundamentals and CBSE CS curriculum for Class 8–12 students.",
            "provider": {
              "@type": "EducationalOrganization",
              "name": "SSS Achievers",
              "url": "https://www.sssachievers.com",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "96/B, Badi Bagh",
                "addressLocality": "Ghazipur",
                "addressRegion": "Uttar Pradesh",
                "postalCode": "233001",
                "addressCountry": "IN"
              },
              "telephone": "+919918519991"
            },
            "url": "https://www.sssachievers.com/courses/computer-science",
            "educationalLevel": "Class 8–12",
            "teaches": ["Python", "C++", "MS Office", "Web Fundamentals", "Database Basics", "CBSE Computer Science"],
            "hasCourseInstance": {
              "@type": "CourseInstance",
              "courseMode": "onsite",
              "location": {
                "@type": "Place",
                "name": "SSS Achievers, Ghazipur",
                "address": "96/B, Badi Bagh, Ghazipur, UP 233001"
              }
            }
          }
        `}</script>
      </Helmet>

      <main
        className="min-h-screen bg-white text-slate-900 antialiased"
        style={{
          "--primary": PRIMARY,
          "--primary-50": "rgba(0,151,178,0.05)",
          "--primary-10": "rgba(0,151,178,0.10)",
          "--secondary": SECONDARY,
          "--secondary-10": "rgba(252,191,10,0.10)",
        }}
      >
        {/* ── Hero ── */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[var(--primary-50)] via-white to-[var(--secondary-10)] pt-10 pb-12 md:pt-20 md:pb-28">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-[var(--secondary)]/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[var(--primary)]/10 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeUp}
                className="space-y-6"
              >
                <div className="inline-block px-4 py-2 rounded-full bg-[var(--secondary)]/20">
                  <span className="text-base font-semibold" style={{ color: PRIMARY }}>
                    💻 Computer Science Course
                  </span>
                </div>

                <h1 className="text-4xl md:text-5xl font-semibold leading-tight">
                  Master Technology. Build the Future.
                </h1>

                <p className="text-base text-slate-700 max-w-2xl">
                  From MS Office basics to Python programming and web fundamentals — SSS Achievers'
                  Computer Science program equips students of Class 8–12 with real-world digital
                  skills, CBSE-aligned theory, and hands-on practical experience. Prepare for
                  board exams and build a strong tech foundation for tomorrow.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={openAdmissionEnquiry}
                    className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-[var(--primary)] text-white font-semibold shadow hover:shadow-lg hover:-translate-y-0.5 transition"
                  >
                    Start Free Demo
                  </button>
                  <button
                    onClick={openAdmissionEnquiry}
                    className="inline-flex items-center justify-center px-6 py-3 rounded-lg border-2 border-[var(--primary)] text-[var(--primary)] font-semibold hover:bg-[var(--primary)]/5 transition"
                  >
                    Enroll Now
                  </button>
                </div>

                <div className="flex flex-wrap gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-[var(--secondary)]" />
                    <span className="font-medium">15–20 Students</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-[var(--secondary)]" />
                    <span className="font-medium">Class 8 – 12</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Monitor className="w-5 h-5 text-[var(--secondary)]" />
                    <span className="font-medium">AI Smart Classroom</span>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6 }}
                className="flex items-center justify-center"
              >
                {/* Decorative visual */}
                <div className="w-full max-w-sm aspect-square rounded-3xl bg-gradient-to-br from-[var(--primary)]/10 to-[var(--secondary)]/10 border-2 border-[var(--primary)]/10 flex flex-col items-center justify-center gap-6 p-8">
                  <div className="grid grid-cols-3 gap-4">
                    {[Code2, Database, Globe, Cpu, Monitor, FileCode].map((Icon, i) => (
                      <div
                        key={i}
                        className="w-14 h-14 rounded-2xl bg-white shadow-md flex items-center justify-center border border-gray-100"
                      >
                        <Icon className="w-7 h-7 text-[var(--primary)]" />
                      </div>
                    ))}
                  </div>
                  <p className="text-center text-sm font-semibold text-slate-600">
                    Practical · Hands-on · CBSE Aligned
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ── Topics / Cards ── */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-bold"
              >
                What You'll Learn
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto"
              >
                A structured curriculum from digital basics to advanced programming
              </motion.p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: "💻",
                  title: "MS Office Suite",
                  desc: "Word, Excel, PowerPoint — essential productivity tools for every student.",
                },
                {
                  icon: "🐍",
                  title: "Python Programming",
                  desc: "Beginner-friendly Python — syntax, logic, functions & mini projects.",
                },
                {
                  icon: "⚙️",
                  title: "C++ Basics",
                  desc: "Object-oriented concepts, loops, arrays & problem solving in C++.",
                },
                {
                  icon: "🌐",
                  title: "Web Fundamentals",
                  desc: "HTML & CSS basics to understand how websites are built.",
                },
                {
                  icon: "🗄️",
                  title: "Database (SQL Intro)",
                  desc: "Understanding data, tables, queries — the backbone of every app.",
                },
                {
                  icon: "📚",
                  title: "CBSE CS Theory",
                  desc: "Full NCERT/CBSE Computer Science curriculum — Class 8 through 12.",
                },
              ].map((topic, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                  custom={i}
                  variants={fadeUp}
                  className="p-6 border-2 border-gray-100 hover:border-[var(--primary)]/50 hover:shadow-lg transition-all duration-300 group rounded-2xl"
                >
                  <div className="text-4xl mb-4 group-hover:scale-110 transition-transform">
                    {topic.icon}
                  </div>
                  <h3 className="text-lg font-semibold mb-2 text-slate-800">{topic.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{topic.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Why SSS for CS ── */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[var(--primary-50)] to-[var(--secondary-10)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <motion.h2
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-bold"
              >
                Why Choose SSS Achievers for Computer Science?
              </motion.h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  icon: Brain,
                  title: "Practical-First Teaching",
                  desc: "Every concept is reinforced with hands-on lab exercises and real examples.",
                },
                {
                  icon: TrendingUp,
                  title: "Board-Exam Ready",
                  desc: "CBSE-aligned curriculum with full syllabus coverage and past-paper practice.",
                },
                {
                  icon: Users,
                  title: "Small Batches",
                  desc: "Max 15–20 students per batch for individual attention and doubt clearing.",
                },
                {
                  icon: Zap,
                  title: "AI Smart Classroom",
                  desc: "Learn in a tech-enabled environment with projectors, labs and digital tools.",
                },
                {
                  icon: Award,
                  title: "Project-Based Learning",
                  desc: "Students build mini projects that showcase real programming skills.",
                },
                {
                  icon: BookOpen,
                  title: "Comprehensive Materials",
                  desc: "Printed + digital notes, code samples and practice worksheets provided.",
                },
              ].map((feat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.45 }}
                  className="flex gap-4 p-6 bg-white rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow"
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[var(--primary)]/10 flex items-center justify-center">
                    <feat.icon className="w-6 h-6 text-[var(--primary)]" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold mb-1">{feat.title}</h3>
                    <p className="text-slate-600 text-sm">{feat.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Course Structure ── */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-bold"
              >
                Course Structure & Details
              </motion.h2>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {[
                { label: "Eligible Classes", value: "Class 8 – 12", icon: BookOpen },
                { label: "Batch Size", value: "15–20 Students", icon: Users },
                { label: "Schedule", value: "Mon–Fri, Flexible Slots", icon: Clock },
                { label: "Lab Sessions", value: "Weekly Practicals", icon: Monitor },
                { label: "Fee", value: "Price on request", icon: Award },
                { label: "Installments", value: "Available", icon: CheckCircle2 },
              ].map((item, i) => {
                const isFee = item.label === "Fee";
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.06, duration: 0.4 }}
                    className={`p-6 border-2 border-gray-100 rounded-xl bg-gradient-to-br from-white to-[var(--primary-50)] ${isFee ? "cursor-pointer hover:shadow-md" : ""}`}
                    onClick={() => isFee && (window.location.href = "/contact")}
                    role={isFee ? "button" : undefined}
                    tabIndex={isFee ? 0 : undefined}
                  >
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-lg bg-[var(--secondary)]/10">
                        <item.icon className="w-6 h-6 text-[var(--primary)]" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500 font-medium">{item.label}</p>
                        <p className="text-lg font-bold mt-1">{item.value}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Learning outcomes */}
            <div className="bg-gradient-to-r from-[var(--primary-10)] to-[var(--secondary-10)] rounded-3xl p-8 border-2 border-[var(--primary)]/10">
              <h3 className="text-2xl font-bold mb-6">Learning Outcomes</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  "Write clean Python and C++ programs confidently",
                  "Use MS Office tools professionally for school and work",
                  "Understand web basics — HTML, CSS and how internet works",
                  "Score well in CBSE Computer Science theory and practicals",
                  "Build a mini project to showcase at school or events",
                  "Develop logical thinking and algorithmic problem-solving",
                ].map((outcome, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-[var(--primary)] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 font-medium text-sm">{outcome}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Class-wise breakdown cards ── */}
        <section className="py-16 md:py-20 bg-[var(--primary-50)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-bold"
              >
                Class-wise Curriculum Overview
              </motion.h2>
              <p className="mt-3 text-slate-600 max-w-xl mx-auto">
                Tailored content for each class — no one-size-fits-all approach
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  class: "Class 8",
                  badge: "8th",
                  color: "border-blue-200 bg-blue-50",
                  badgeColor: "bg-blue-100 text-blue-700",
                  topics: ["Introduction to Computers", "MS Paint & Office basics", "Internet Safety", "Scratch programming"],
                },
                {
                  class: "Class 9",
                  badge: "9th",
                  color: "border-emerald-200 bg-emerald-50",
                  badgeColor: "bg-emerald-100 text-emerald-700",
                  topics: ["MS Office advanced", "Python basics (NCERT)", "HTML introduction", "Data storage concepts"],
                },
                {
                  class: "Class 10",
                  badge: "10th",
                  color: "border-amber-200 bg-amber-50",
                  badgeColor: "bg-amber-100 text-amber-700",
                  topics: ["Python programming", "SQL intro", "Cybersecurity basics", "Board exam practicals"],
                },
                {
                  class: "Class 11",
                  badge: "11th",
                  color: "border-violet-200 bg-violet-50",
                  badgeColor: "bg-violet-100 text-violet-700",
                  topics: ["C++ / Python (NCERT CS)", "Data structures basics", "Boolean logic", "Database SQL"],
                },
                {
                  class: "Class 12",
                  badge: "12th",
                  color: "border-rose-200 bg-rose-50",
                  badgeColor: "bg-rose-100 text-rose-700",
                  topics: ["Advanced Python & C++", "File handling", "Networking basics", "Board practical prep"],
                },
              ].map((card, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                  custom={i}
                  variants={fadeUp}
                  className={`rounded-2xl border-2 p-6 ${card.color}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${card.badgeColor}`}>
                      {card.badge}
                    </span>
                    <h3 className="font-bold text-slate-800">{card.class} Curriculum</h3>
                  </div>
                  <ul className="space-y-2">
                    {card.topics.map((t, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-[var(--primary)] flex-shrink-0 mt-0.5" />
                        {t}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="py-16 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <motion.h2
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-bold"
              >
                Frequently Asked Questions
              </motion.h2>
            </div>
            <FAQAccordion
              faqs={[
                { q: "Is this course available for all classes 8–12?", a: "Yes — we offer tailored Computer Science coaching for every class from 8th to 12th, aligned with the CBSE/NCERT curriculum." },
                { q: "Do I need a personal computer or laptop?", a: "No — SSS Achievers has a computer lab with all necessary equipment. Students can practice during lab sessions at the institute." },
                { q: "Which programming language is taught?", a: "We teach Python (primary), C++ (for Class 11–12) and introduce web basics (HTML/CSS). The language depends on the class and CBSE syllabus." },
                { q: "Are there practicals and projects?", a: "Yes — every student completes hands-on practicals and a mini project as part of the curriculum." },
                { q: "Is there a free demo class available?", a: "Yes — you can attend a free demo class before enrolling to see our teaching style and lab facilities." },
              ]}
            />
          </div>
        </section>
      </main>
    </>
  );
}
